import { useEffect, useRef } from "react";
import {
	type Params,
	type Location as RouterLocation,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import {
	hasInAppHistory,
	resolveBackAction,
} from "web_myplann/Shared/Utils/backNavigation";
import { device } from "web_myplann/Utils/device";
import { isWebViewMessage } from "web_myplann/Utils/isWebViewMessage";
import { WebViewMessageTypes } from "web_myplann/Utils/WebViewModel";

export const useNavigateBack = () => {
	const navigate = useNavigate();

	const location = useLocation();
	const params = useParams();

	const locationRef = useRef<{
		location: RouterLocation;
		params: Readonly<Params<string>>;
	}>({ location, params });

	if (
		locationRef.current.location !== location ||
		locationRef.current.params !== params
	) {
		locationRef.current = { location, params };
	}

	useEffect(() => {
		const messageListener = (nativeEvent: MessageEvent) => {
			const currentLocation = locationRef.current;

			if (!isWebViewMessage(nativeEvent.data)) {
				return;
			}

			if (nativeEvent.data.type === WebViewMessageTypes.NAVIGATE_BACK_REQUEST) {
				const canGoBack = hasInAppHistory(
					currentLocation.location.key,
					window.history.length,
				);

				const action = resolveBackAction({
					pathname: currentLocation.location.pathname,
					canGoBack,
				});

				switch (action.type) {
					case "native":
						device.navigateBackRequest();
						return;
					case "history":
						navigate(-1);
						return;
					case "navigate":
						navigate(action.to, { replace: true });
						return;
					case "none":
						return;
				}
			}
		};

		window.addEventListener("message", messageListener);
		return () => {
			window.removeEventListener("message", messageListener);
		};
	}, [navigate, locationRef]);
};
